/**
 * FormaVeraView — the Vera analysis surface inside the Forma chrome, shown
 * AFTER the user clicks "Check with Vera" on the Files screen. This is the
 * former body of FormaApp: same loaders + the same live SheetViewer /
 * Requirements / Violations panels as standalone, wrapped in the cosmetic
 * Forma shell. `onBackToFiles` returns to the Files landing.
 */
import { useEffect, useMemo } from "preact/hooks";
import { isAuthDisabled } from "../../auth/apsConfig";
import { isUniversalSheet } from "../../data/sheets";
import { useAccLiveModel } from "../../hooks/useAccLiveModel";
import { useBcaSheetAutoSwitch } from "../../hooks/useBcaSheetMode";
import { useLiveAccAnalysis } from "../../hooks/useLiveAccAnalysis";
import { liveSheetByOntologyType } from "../../lib/liveSheetRouting";
import { useAccBrowseStore } from "../../stores/accBrowseStore";
import { useAccModelStore } from "../../stores/accModelStore";
import {
  computeLiveViolations,
  useEngineStore,
} from "../../stores/engineStore";
import {
  geometryViolations,
  useGeometryStore,
} from "../../stores/geometryStore";
import { useCurrentProject } from "../../stores/projectStore";
import { usePropertiesStore } from "../../stores/propertiesStore";
import { useUserFactStore } from "../../stores/userFactStore";
import { useViewerStore } from "../../stores/viewerStore";
import { useViolationsStore } from "../../stores/violationsStore";
import { SheetViewer } from "../SheetViewer";
import { FormaLeftRail } from "./FormaLeftRail";
import { FormaRightDrawer } from "./FormaRightDrawer";
import { FormaSubBar } from "./FormaSubBar";
import { FORMA } from "./FormaTokens";
import { FormaTopBar } from "./FormaTopBar";

export function FormaVeraView({
  onBackToFiles,
}: {
  onBackToFiles: () => void;
}) {
  const project = useCurrentProject();
  const accSelection = useAccBrowseStore(s => s.selection);
  const inferredJurisdictionIds = useEngineStore(
    s => s.inferredJurisdictionIds
  );
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const accViewables = useAccModelStore(s => s.viewables);
  const currentViewableId = useAccModelStore(s => s.currentViewableId);
  const accModelError = useAccModelStore(s => s.error);

  const loadProperties = usePropertiesStore(s => s.loadFromUrl);
  const loadEngine = useEngineStore(s => s.loadFromUrls);
  const loadRulesFromUrl = useEngineStore(s => s.loadRulesFromUrl);
  const setCurrentSheet = useViewerStore(s => s.setCurrentSheet);
  const viewer = useViewerStore(s => s.viewer);
  const viewerReady = useViewerStore(s => s.ready);
  const currentSheetId = useViewerStore(s => s.currentSheetId);
  const engineLoaded = useEngineStore(s => s.loaded);
  const rules = useEngineStore(s => s.rules);
  const elements = useEngineStore(s => s.elements);
  const enabledJurisdictions = useEngineStore(s => s.enabledJurisdictions);
  const setActive = useViolationsStore(s => s.setActive);
  const userFactOverrides = useUserFactStore(s => s.overrides);
  const loadUserFacts = useUserFactStore(s => s.loadProject);
  const geometryRows = useGeometryStore(s => s.rows);
  const geometryStatus = useGeometryStore(s => s.status);
  const runGeometryChecks = useGeometryStore(s => s.runGeometryChecks);
  const clearGeometry = useGeometryStore(s => s.clear);

  const accMode = !isAuthDisabled();
  const hasAccJurisdiction = inferredJurisdictionIds.length > 0;
  const liveAccActive = accMode && !!accSelection && hasAccJurisdiction;
  const inferredJurisdictionKey = inferredJurisdictionIds.join("|");

  useAccLiveModel();
  useLiveAccAnalysis();
  useBcaSheetAutoSwitch();

  const sheetRouting = useMemo(() => {
    if (viewerUrn && accViewables.length > 0) {
      return liveSheetByOntologyType(accViewables);
    }
    return project.defaultSheetByOntologyType;
  }, [viewerUrn, accViewables, project.defaultSheetByOntologyType]);

  useEffect(() => {
    if (!accMode) return;
    if (!hasAccJurisdiction) {
      useEngineStore.setState({
        loaded: false,
        loadError: null,
        elements: [],
        rules: [],
        jurisdictions: [],
        enabledJurisdictions: {},
      });
      setActive([]);
      return;
    }
    void loadRulesFromUrl("/rule-pack.json", { inferredJurisdictionIds });
  }, [
    accMode,
    hasAccJurisdiction,
    inferredJurisdictionKey,
    loadRulesFromUrl,
    setActive,
  ]);

  useEffect(() => {
    if (!accMode) return;
    clearGeometry();
  }, [accMode, accSelection?.itemId, clearGeometry]);

  useEffect(() => {
    if (accMode) return;
    loadProperties(project.propertiesUrl);
    void loadEngine("/rule-pack.json", project.elementFactsUrl, {
      inferredJurisdictionIds: project.inferredJurisdictionIds,
    });
    setCurrentSheet(project.defaultSheetId);
    clearGeometry();
    loadUserFacts(project.id);
  }, [
    accMode,
    project,
    loadProperties,
    loadEngine,
    setCurrentSheet,
    clearGeometry,
    loadUserFacts,
  ]);

  useEffect(() => {
    if (!engineLoaded || !viewerReady || !viewer) return;
    if (!enabledJurisdictions["sg-bca"]) {
      clearGeometry();
      return;
    }
    const onUniversalView = viewerUrn
      ? accViewables.find(v => v.id === currentViewableId)?.kind === "3d"
      : isUniversalSheet(currentSheetId);
    if (!onUniversalView) return;
    void runGeometryChecks(viewer);
  }, [
    engineLoaded,
    viewerReady,
    viewer,
    viewerUrn,
    accViewables,
    currentViewableId,
    currentSheetId,
    enabledJurisdictions,
    runGeometryChecks,
    clearGeometry,
  ]);

  useEffect(() => {
    if (!engineLoaded) return;
    const live = computeLiveViolations(
      useEngineStore.getState(),
      sheetRouting,
      userFactOverrides,
      liveAccActive ? "acc-live" : project.id
    );
    const bcaOn = !!enabledJurisdictions["sg-bca"];
    const geo =
      bcaOn && geometryStatus === "done"
        ? geometryViolations(geometryRows)
        : [];
    setActive([...live, ...geo]);
  }, [
    engineLoaded,
    rules,
    elements,
    enabledJurisdictions,
    geometryRows,
    geometryStatus,
    userFactOverrides,
    setActive,
    sheetRouting,
    liveAccActive,
    project.id,
  ]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        background: FORMA.pageBg,
        color: FORMA.textPrimary,
        fontSize: 13,
      }}
    >
      <FormaTopBar />
      <FormaSubBar />
      {accSelection && (
        <div
          style={{
            padding: "8px 16px",
            background: accModelError ? "#fef2f2" : "#f0f4ff",
            borderBottom: `1px solid ${FORMA.borderSoft}`,
            fontSize: 12,
            color: accModelError ? "#991b1b" : "#374151",
          }}
        >
          ACC model: <strong>{accSelection.fileName}</strong> in{" "}
          {accSelection.projectName} ({accSelection.hubName})
          {accModelError ? ` — ${accModelError}` : " — live analysis from ACC"}
        </div>
      )}
      <main style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <FormaLeftRail
          activeLabel="Vera Compliance"
          onFilesClick={onBackToFiles}
        />
        <section
          aria-label="Sheet canvas"
          style={{
            flex: 1,
            position: "relative",
            background: FORMA.pageBg,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SheetViewer />
        </section>
        <FormaRightDrawer />
      </main>
    </div>
  );
}
